import { z } from 'zod';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * MARKA MOCKUP SETİ — logodan gerçekçi ürün/marka sunumları (4 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Kullanıcı isteği: *"chatgpt'de var ya, /brandasset yazıp logonu atınca
 * mockuplar yapıyor… tamamını biz de ekleyelim"*. Sohbetteki "Marka → Mockup
 * seti" akışının ve `generate_creative` aracının `brand-mockup` becerisinin
 * ortak sözleşmesi.
 *
 * ---------------------------------------------------------------------------
 * Nasıl üretiliyor
 * ---------------------------------------------------------------------------
 * Logo TABAN görsel (`sourceAssetKey`) olarak düzenleme ucuna gidiyor ve her
 * varyant listedeki BİR nesne: 4 öğe = 4 varyant = 4 ayrı görsel, her biri
 * kendi fiyatıyla (`AD_IMAGE` birim × öğe sayısı). Reçeteler (nesnenin tarifi,
 * baskı tekniği, stil) SUNUCUDA: `@mentorivo/shared/brand-mockup-recipes` —
 * sahne şablonlarıyla aynı karar, istemci prompt görmez.
 *
 * Bu dosya barrel'da: arayüz öğe kimliklerini ve sırasını buradan okuyor,
 * adlar çeviri dosyasında (`dashboard.agentChat.flows.set.mockupItem.*`).
 */

export const BRAND_MOCKUP_ITEMS = [
  'tshirt',
  'hoodie',
  'cap',
  'mug',
  'business_card',
  'stationery',
  'tote_bag',
  'shopping_bag',
  'packaging_box',
  'storefront_sign',
  'billboard',
  'phone_screen',
] as const;
export type BrandMockupItem = (typeof BRAND_MOCKUP_ITEMS)[number];

/**
 * Sunum dili — üç tane, çünkü üçü de ayrı bir İŞE yarıyor:
 *
 *   studio     katalog/sunum dosyası — nötr fon, nesne kahraman
 *   lifestyle  sosyal medya — gerçek mekân, gün ışığı, eller (yüz yok)
 *   premium    lansman/yatırımcı sunumu — koyu fon, dramatik ışık
 */
export const BRAND_MOCKUP_STYLES = ['studio', 'lifestyle', 'premium'] as const;
export type BrandMockupStyle = (typeof BRAND_MOCKUP_STYLES)[number];

/**
 * Bir sette en fazla kaç öğe.
 *
 * 🔴 `MAX_VARIANTS` (creative.ts) ile AYNI olmak zorunda — öğe başına bir
 * varyant üretiliyor ve üretim şeması varyantı o tavanla sınırlıyor. Buraya
 * import edilmedi çünkü `creative.ts` bu dosyayı import ediyor (döngü);
 * eşitliği API'deki spec denetliyor.
 */
export const BRAND_MOCKUP_MAX_ITEMS = 4;

/** Akış açıldığında işaretli gelen dört öğe — en sık istenen "marka seti". */
export const BRAND_MOCKUP_DEFAULT_ITEMS: readonly BrandMockupItem[] = [
  'tshirt',
  'mug',
  'business_card',
  'tote_bag',
];

/**
 * Üretim isteğindeki hâli — `creativeGenerateSchema.mockup`.
 *
 * Öğeler tekil olmalı: aynı nesneyi iki kez istemek aynı görseli iki kez
 * ödemek demek ("başka açıdan" isteniyorsa o bir yeniden üretim).
 */
export const brandMockupParamsSchema = z.object({
  items: z
    .array(z.enum(BRAND_MOCKUP_ITEMS))
    .min(1)
    .max(BRAND_MOCKUP_MAX_ITEMS)
    .refine((items) => new Set(items).size === items.length, { message: 'Aynı mockup iki kez seçilemez' }),
  style: z.enum(BRAND_MOCKUP_STYLES).default('studio'),
});
export type BrandMockupParams = z.infer<typeof brandMockupParamsSchema>;
